import {
  FC,
  useRef,
  useState,
  useEffect,
  MouseEvent as ReactMouseEvent,
  useCallback,
} from 'react';
import cn from 'classnames';
import Image from 'next/legacy/image';
import { UiTitle, UiText } from '@frontend/znanie-uikit';

import { useAuth } from 'components/context/AuthContext';
import HtmlContent from 'components/common/HtmlContent';

import { trackAnalyticsData, AnalyticsEvent } from 'utils/analytics';

import { UserScore } from '../UserScore';
import { CinemaWeek } from '../CinemaWeek';
import { UserMultiplier } from '../UserMultiplier';

import { useContentAnalytics } from '../../DailyQuestion.hooks';

import * as T from './QuestionBlock.types';

import css from './QuestionBlock.module.scss';

export const QuestionBlock: FC<T.IQuestionBlock> = (props) => {
  const { isAuthorized } = useAuth();

  const answerRef = useRef<T.TAnswer>();
  const questionRef = useRef<HTMLDivElement>(null);
  const isDisabledRef = useRef<Boolean>(false);

  const [choosed, setChoosed] = useState<string>();

  const answerClickHandler = useCallback(
    (e: ReactMouseEvent<HTMLDivElement, MouseEvent>, answer: T.TAnswer) => {
      answerRef.current = answer;

      if (isAuthorized) {
        if (isDisabledRef.current) {
          return;
        }

        isDisabledRef.current = true;
        setChoosed(answer?.id);

        try {
          props.onAnswerClick(answer);
        } catch (err) {
          isDisabledRef.current = false;
        }
        // TODO: добавить точное время анимации
      } else {
        ZnanieSDK.showAuth();

        trackAnalyticsData(AnalyticsEvent.DAILY_QUESTION_REGISTRATION_SHOW);
      }
    },
    [isAuthorized]
  );

  useEffect(() => {
    const loginHandler = async () => {
      await props.refetchData();

      if (answerRef.current) {
        await props.onAnswerClick(answerRef.current);
      }
    };

    ZnanieSDK && ZnanieSDK.on('login', loginHandler);

    return () => {
      ZnanieSDK.off('login', loginHandler);
    };
  }, []);

  useContentAnalytics(questionRef, [props.question]);

  return (
    <div className={css.container}>
      <div className={css.rightBlock}>
        <div className={css.userScoreWrapper}>
          <UserScore score={props.userScore} />

          <UserMultiplier multiplier={props.multiplier} />
        </div>

        <div className={css.mediaBlock}>
          {props.isCinemaWeek && <CinemaWeek />}

          {props.image && (
            <div className={css.imageBlock}>
              <div
                className={cn(
                  css.imageWrapper,
                  props.isDarkTheme && css.darkTheme
                )}
              >
                <Image
                  src={props.image}
                  layout="fill"
                  alt="Иллюстрация вопроса"
                  objectFit="contain"
                  objectPosition="top"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={css.leftBlock}>
        <UiTitle
          size="big"
          className={cn(css.title, props.isDarkTheme && css.darkTheme)}
        >
          <HtmlContent html={props.question} ref={questionRef} />
        </UiTitle>

        <div className={css.answersWrapper}>
          {props.answers?.map((answer) => (
            <UiText
              key={answer?.id}
              onClick={(e) => answerClickHandler(e, answer)}
              className={cn(
                css.answerItemWrapper,
                choosed === answer?.id && css.pressed,
                props.isDarkTheme && css.darkTheme
              )}
            >
              {answer?.text}
            </UiText>
          ))}
        </div>
      </div>
    </div>
  );
};
